import { Group, Stack, Text } from '@mantine/core';
import classes from './dot-plot.module.css';

export interface DotPlotRow {
  label: string;
  /** The lower mark — the weaker measure. */
  from: number;
  /** The upper mark — the one with the quality bar on it. */
  to: number;
  /** Small print under the label. */
  detail?: string;
}

interface DotPlotProps {
  rows: DotPlotRow[];
  /** Top of the scale. Shared across rows so they compare. */
  max: number;
  fromLabel: string;
  toLabel: string;
}

const format = new Intl.NumberFormat('en-GB');

/**
 * Two marks per row on a shared scale, with the gap between them drawn.
 *
 * A single progress bar can only show one of the two numbers, and the gap is
 * the entire point here: the distance between opening a surface and actually
 * using it is the difference between measuring navigation and measuring the
 * thing the product claims to train.
 */
export function DotPlot({ rows, max, fromLabel, toLabel }: DotPlotProps) {
  const position = (value: number) => (max === 0 ? 0 : (value / max) * 100);

  return (
    <Stack gap="sm">
      <Group gap="lg" justify="flex-end">
        <Group gap={6} wrap="nowrap">
          <span className={classes.keyFrom} />
          <Text size="xs" c="dimmed">
            {fromLabel}
          </Text>
        </Group>
        <Group gap={6} wrap="nowrap">
          <span className={classes.keyTo} />
          <Text size="xs" c="dimmed">
            {toLabel}
          </Text>
        </Group>
      </Group>

      {rows.map((row) => {
        const start = position(Math.min(row.from, row.to));
        const end = position(Math.max(row.from, row.to));

        return (
          <div key={row.label} className={classes.row}>
            <Stack gap={0} className={classes.meta}>
              <Text size="sm" fw={600}>
                {row.label}
              </Text>
              {row.detail && (
                <Text size="xs" c="dimmed" lh={1.3}>
                  {row.detail}
                </Text>
              )}
            </Stack>

            <div className={classes.track}>
              <div className={classes.rule} />
              <div
                className={classes.span}
                style={{ left: `${start}%`, width: `${end - start}%` }}
              />
              <span
                className={classes.markFrom}
                style={{ left: `${position(row.from)}%` }}
              />
              <span
                className={classes.markTo}
                style={{ left: `${position(row.to)}%` }}
              />
            </div>

            <Text size="sm" className={classes.value}>
              {format.format(row.to)}
              <Text span size="xs" c="dimmed">
                {' '}
                / {format.format(row.from)}
              </Text>
            </Text>
          </div>
        );
      })}
    </Stack>
  );
}
